import React, { useState, useEffect } from 'react';
import { assetGroupService, AssetGroup } from '../../services/assetGroupService';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

export const AssetGroupsManagement: React.FC = () => {
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAssetGroup, setEditingAssetGroup] = useState<AssetGroup | null>(null);
  const [viewingAssetGroup, setViewingAssetGroup] = useState<AssetGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    assetID: '',
    lanetypeid: '',
    laneassignmentID: ''
  });
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();

  useEffect(() => {
    // Only load asset groups if the service is available
    if (assetGroupService && typeof assetGroupService.getAllAssetGroups === 'function') {
      loadAssetGroups();
    } else {
      console.warn('⚠️ Asset group service not available, skipping load');
      setError('Asset group service not available');
    }
  }, []);

  // Safety check to ensure assetGroups is always an array
  const safeAssetGroups = Array.isArray(assetGroups) ? assetGroups : [];

  const loadAssetGroups = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      // Loading asset groups
      
      // Check if assetGroupService is available
      if (!assetGroupService || typeof assetGroupService.getAllAssetGroups !== 'function') {
        console.error('❌ Asset group service not available');
        setError('Asset group service not available');
        setAssetGroups([]);
        return;
      }
      
      // Check if we're in development mode and use mock data if needed
      const isDevelopment = process.env.NODE_ENV === 'development';
              // Environment detected
      
      let response;
      try {
        response = await assetGroupService.getAllAssetGroups();
      } catch (apiError) {
        console.warn('⚠️ API call failed, this might be expected in development:', apiError);
        
        // In development, provide mock data if API fails
        if (isDevelopment) {
          const mockAssetGroups: AssetGroup[] = [
            {
              id: 1,
              assetID: 101,
              lanetypeid: 1,
              laneassignmentID: 1,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 2,
              assetID: 102,
              lanetypeid: 2,
              laneassignmentID: 2,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ];
          // Using mock asset groups for development
          setAssetGroups(mockAssetGroups);
          return;
        } else {
          throw apiError; // Re-throw in production
        }
      }
      
              // Asset groups API response received
      
      // Ensure response is an array
      if (Array.isArray(response)) {
        // Response is array, setting asset groups
        setAssetGroups(response);
      } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as any).data)) {
        // Handle case where API returns { data: [...] }
                  // Response has data property
        setAssetGroups((response as any).data);
      } else if (response && typeof response === 'object' && 'results' in response && Array.isArray((response as any).results)) {
        // Handle case where API returns { results: [...] }
                  // Response has results property
        setAssetGroups((response as any).results);
      } else {
        console.warn('⚠️ Unexpected asset groups response format:', response);
        const errorMsg = `Unexpected response format: ${typeof response}`;
        setError(errorMsg);
        setAssetGroups([]);
      }
    } catch (error) {
      console.error('❌ Failed to load asset groups:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to load asset groups: ${errorMessage}`);
      showToast('Failed to load asset groups', 'error');
      setAssetGroups([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.assetID) {
      errors.assetID = 'Please select an asset';
    }
    
    if (!formData.lanetypeid) {
      errors.lanetypeid = 'Please select a lane type';
    }
    
    if (!formData.laneassignmentID) {
      errors.laneassignmentID = 'Please select a lane assignment';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = {
        assetID: parseInt(formData.assetID),
        lanetypeid: parseInt(formData.lanetypeid),
        laneassignmentID: parseInt(formData.laneassignmentID)
      };
      
      if (editingAssetGroup) {
        await assetGroupService.updateAssetGroup(editingAssetGroup.id, submitData);
        showToast('Asset group updated successfully!', 'success');
      } else {
        await assetGroupService.createAssetGroup(submitData);
        showToast('Asset group created successfully!', 'success');
      }
      
      resetForm();
      await loadAssetGroups();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save asset group';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assetGroup: AssetGroup) => {
    setEditingAssetGroup(assetGroup);
    setFormData({
      assetID: assetGroup.assetID.toString(),
      lanetypeid: assetGroup.lanetypeid.toString(),
      laneassignmentID: assetGroup.laneassignmentID.toString()
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const handleView = (assetGroup: AssetGroup) => {
    setViewingAssetGroup(assetGroup);
  };

  const handleDelete = async (assetGroup: AssetGroup) => {
    if (!window.confirm(`Are you sure you want to delete this asset group?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await assetGroupService.deleteAssetGroup(assetGroup.id);
      showToast('Asset group deleted successfully!', 'success');
      await loadAssetGroups();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete asset group';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      assetID: '',
      lanetypeid: '',
      laneassignmentID: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingAssetGroup(null);
  };

  // Safety check to ensure the component renders even if there are issues
  try {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Asset Groups Management</h1>
            <p className="text-gray-400 text-sm">Manage asset groups and their assignments</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center">
            {permissionChecks.canUpdateAssetGroups() && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
              >
                {showAddForm ? 'Cancel' : 'Add Asset Group'}
              </button>
            )}
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && permissionChecks.canUpdateAssetGroups() && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">
              {editingAssetGroup ? 'Edit Asset Group' : 'Add New Asset Group'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Asset ID *
                  </label>
                  <input
                    type="number"
                    name="assetID"
                    value={formData.assetID}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter Asset ID"
                    required
                  />
                  {formErrors.assetID && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.assetID}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Lane Type ID *
                  </label>
                  <input
                    type="number"
                    name="lanetypeid"
                    value={formData.lanetypeid}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter Lane Type ID"
                    required
                  />
                  {formErrors.lanetypeid && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.lanetypeid}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Lane Assignment ID *
                  </label>
                  <input
                    type="number"
                    name="laneassignmentID"
                    value={formData.laneassignmentID}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter Lane Assignment ID"
                    required
                  />
                  {formErrors.laneassignmentID && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.laneassignmentID}</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors text-sm font-medium"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingAssetGroup ? 'Update Asset Group' : 'Create Asset Group')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded transition-colors text-sm font-medium"
                  disabled={loading}
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Asset Groups List */}
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">Asset Groups List</h3>
          
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-md">
              <p className="text-sm text-red-400">Error: {error}</p>
              <button 
                onClick={() => {
                  setError(null);
                  loadAssetGroups();
                }}
                className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors"
              >
                Retry
              </button>
            </div>
          )}
          
          {loading && !showAddForm ? (
            <div className="text-center py-8">
              <div className="loading-spinner mx-auto mb-2"></div>
              <p className="text-gray-400">Loading asset groups...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {safeAssetGroups.map((assetGroup) => (
                <div key={assetGroup.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                  <div className="flex-1">
                    <div className="font-medium text-white">Asset Group #{assetGroup.id}</div>
                    <div className="text-sm text-gray-400">
                      Asset ID: {assetGroup.assetID}
                    </div>
                    <div className="text-sm text-yellow-300">Lane Type ID: {assetGroup.lanetypeid}</div>
                    <div className="text-sm text-blue-300">Lane Assignment ID: {assetGroup.laneassignmentID}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Created: {new Date(assetGroup.created_at).toLocaleDateString()}
                    </div>
                    {assetGroup.updated_at && (
                      <div className="text-xs text-gray-500">
                        Updated: {new Date(assetGroup.updated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Edit Button */}
                    {permissionChecks.canUpdateAssetGroups() && (
                      <button 
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                        onClick={() => handleEdit(assetGroup)}
                        disabled={loading}
                      >
                        Edit
                      </button>
                    )}
                    
                    {/* View Button - Always visible if user can view asset groups */}
                    <button 
                      className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleView(assetGroup)}
                      disabled={loading}
                    >
                      View
                    </button>
                    
                    {/* Delete Button */}
                    {permissionChecks.canUpdateAssetGroups() && (
                      <button 
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors"
                        onClick={() => handleDelete(assetGroup)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {safeAssetGroups.length === 0 && !loading && !error && (
                <div className="text-center py-8 text-gray-400">
                  No asset groups found. Create your first asset group to get started.
                </div>
              )}
            </div>
          )}
        </div>

        {/* View Modal */}
        {viewingAssetGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Asset Group Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-400">ID:</span>
                  <span className="ml-2 text-white">{viewingAssetGroup.id}</span>
                </div>
                <div>
                  <span className="text-gray-400">Asset ID:</span>
                  <span className="ml-2 text-white">{viewingAssetGroup.assetID}</span>
                </div>
                <div>
                  <span className="text-gray-400">Lane Type ID:</span>
                  <span className="ml-2 text-white">{viewingAssetGroup.lanetypeid}</span>
                </div>
                <div>
                  <span className="text-gray-400">Lane Assignment ID:</span>
                  <span className="ml-2 text-white">{viewingAssetGroup.laneassignmentID}</span>
                </div>
                <div>
                  <span className="text-gray-400">Created:</span>
                  <span className="ml-2 text-white">{new Date(viewingAssetGroup.created_at).toLocaleString()}</span>
                </div>
                {viewingAssetGroup.updated_at && (
                  <div>
                    <span className="text-gray-400">Updated:</span>
                    <span className="ml-2 text-white">{new Date(viewingAssetGroup.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setViewingAssetGroup(null)}
                className="mt-6 w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  } catch (renderError) {
    console.error('❌ Error rendering AssetGroupsManagement:', renderError);
    return (
      <div className="p-6 bg-red-900/50 border border-red-500 rounded-md">
        <h2 className="text-lg font-semibold text-red-400 mb-2">Error Loading Asset Groups</h2>
        <p className="text-red-300 mb-4">
          An error occurred while rendering the Asset Groups Management component.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors"
        >
          Reload Page
        </button>
      </div>
    );
  }
};
